import {
  Dispatch,
  SetStateAction,
  UIEvent,
  memo,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import { GET_BASE_URL_IMAGE } from "../constants/apiEndpoints";
import StickerFairyQuickCreate from "./StickerFairyQuickCreate";
import {
  DEFAULT_STICKER_GRID_HEIGHT,
  DEFAULT_STICKER_GRID_WIDTH,
  STICKER_CELL_MIN,
  STICKER_GRID_GAP,
  filterSortedStickers,
  getStickerGridLayout,
  getStableStickerItemKeys,
  getStickerItemKey,
  getStickerName,
  SEARCH_ALL_TABS_MIN_CHARS,
  getStickerSearchSource,
  getVisibleStickerRange,
  normalizeStickerSortBy,
  omitStickerSearchIndex,
  readCssPixelVar,
  sortStickerCollectionOnce,
  StickerSearchItem,
  StickerSortBy,
} from "../utils/stickerTabsSearch";

type StickerTabPropsType = {
  tabData: any;
  onClickSticker: any;
  onStickerInserted?: () => void;
};

export const getStickerProductImageUrl = (productImage: string) => {
  const value = String(productImage || "").trim();
  if (!value) {
    return "";
  }
  if (/^(https?:\/\/|data:image\/)/i.test(value)) {
    return value;
  }
  if (value.startsWith("/")) {
    return GET_BASE_URL_IMAGE + value;
  }
  return GET_BASE_URL_IMAGE + "/disneyland/images/products_thumbnail/" + value;
};

type StickerVirtualGridProps = {
  items: StickerSearchItem[];
  onAddSticker: (productImage: string, item: StickerSearchItem) => void;
  getStickerKey: (productImage: string, item?: StickerSearchItem) => string;
  recentStickerKey: string;
};

const StickerVirtualGrid = memo(function StickerVirtualGrid({
  items,
  onAddSticker,
  getStickerKey,
  recentStickerKey,
}: StickerVirtualGridProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState({
    width: DEFAULT_STICKER_GRID_WIDTH,
    height: DEFAULT_STICKER_GRID_HEIGHT,
    scrollTop: 0,
    minCell: STICKER_CELL_MIN,
    gap: STICKER_GRID_GAP,
  });
  const pendingScrollTop = useRef(0);
  const scrollFrame = useRef<number | null>(null);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const updateSize = () => {
      const styles = getComputedStyle(element);
      const paddingX =
        (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
      const pane = element.querySelector(".sticker-tabs-pane") as HTMLElement | null;
      const paneStyles = pane ? getComputedStyle(pane) : null;
      const panePaddingX = paneStyles
        ? (parseFloat(paneStyles.paddingLeft) || 0) +
          (parseFloat(paneStyles.paddingRight) || 0)
        : 0;
      const width =
        (pane && Math.max(0, pane.clientWidth - panePaddingX)) ||
        Math.max(0, element.clientWidth - paddingX) ||
        DEFAULT_STICKER_GRID_WIDTH;
      const height = element.clientHeight || DEFAULT_STICKER_GRID_HEIGHT;
      const minCell = readCssPixelVar(pane, "--sticker-cell-min", STICKER_CELL_MIN);
      const gap = readCssPixelVar(pane, "--sticker-grid-gap", STICKER_GRID_GAP);
      setViewport((current) =>
        current.width === width &&
        current.height === height &&
        current.minCell === minCell &&
        current.gap === gap
          ? current
          : { ...current, width, height, minCell, gap }
      );
    };

    updateSize();
    const pane = element.querySelector(".sticker-tabs-pane");
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateSize);
    observer?.observe(element);
    if (pane) {
      observer?.observe(pane);
    }
    window.addEventListener("resize", updateSize);
    window.addEventListener("orientationchange", updateSize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateSize);
      window.removeEventListener("orientationchange", updateSize);
    };
  }, []);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    pendingScrollTop.current = event.currentTarget.scrollTop;
    if (scrollFrame.current !== null) {
      return;
    }
    scrollFrame.current = window.requestAnimationFrame(() => {
      scrollFrame.current = null;
      const scrollTop = pendingScrollTop.current;
      setViewport((current) =>
        Math.abs(current.scrollTop - scrollTop) < 1
          ? current
          : { ...current, scrollTop }
      );
    });
  };

  useEffect(() => {
    return () => {
      if (scrollFrame.current !== null) {
        window.cancelAnimationFrame(scrollFrame.current);
      }
    };
  }, []);

  useEffect(() => {
    pendingScrollTop.current = 0;
    if (scrollFrame.current !== null) {
      window.cancelAnimationFrame(scrollFrame.current);
      scrollFrame.current = null;
    }
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setViewport((current) =>
      current.scrollTop === 0 ? current : { ...current, scrollTop: 0 }
    );
  }, [items]);

  const layout = getStickerGridLayout(
    viewport.width,
    viewport.minCell,
    viewport.gap
  );
  const range = getVisibleStickerRange(
    items.length,
    layout.columns,
    layout.rowHeight,
    viewport.scrollTop,
    viewport.height
  );
  const itemKeys = useMemo(() => getStableStickerItemKeys(items), [items]);
  const visibleItems = items.slice(range.startIndex, range.endIndex);

  return (
    <div
      className="tab-content sticker-tabs-content"
      ref={scrollRef}
      onScroll={handleScroll}
    >
      <div
        className="tab-pane fade active show sticker-tabs-pane"
        style={{
          height: items.length === 0 ? undefined : range.totalHeight,
        }}
      >
        {items.length === 0 ? (
          <p className="sticker-tabs-empty">No stickers found.</p>
        ) : (
          visibleItems.map((item, offset) => {
            const index = range.startIndex + offset;
            const row = Math.floor(index / layout.columns);
            const column = index % layout.columns;
            const stickerKey = itemKeys[index] || getStickerItemKey(item, index);
            const recentKey = getStickerKey(String(item.product_image || ""), item);
            const stickerName = getStickerName(item) || "sticker";
            return (
              <button
                key={stickerKey}
                type="button"
                className={`sticker-tabs-item ${
                  recentStickerKey === recentKey ? "is-recently-inserted" : ""
                }`}
                aria-label={`Insert ${stickerName}`}
                style={{
                  position: "absolute",
                  top: row * layout.rowHeight,
                  left: column * (layout.cellSize + layout.gap),
                  width: layout.cellSize,
                  height: layout.cellSize,
                }}
                onClick={() =>
                  onAddSticker(
                    String(item.product_image || ""),
                    omitStickerSearchIndex(item)
                  )
                }
              >
                <img
                  src={getStickerProductImageUrl(String(item.product_image || ""))}
                  className="img-fluid sticker-tabs-image"
                  alt={stickerName}
                  width={layout.cellSize}
                  height={layout.cellSize}
                  decoding="async"
                />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
});

type NavigationProps = {
  tabs: any[];
  onNavClick: (id: any) => void;
  activeTabId: any;
  showStickerFairyQuickCreate: boolean;
  setShowStickerFairyQuickCreate: Dispatch<SetStateAction<boolean>>;
  setSortedBy: (value: StickerSortBy) => void;
  sortedBy: StickerSortBy;
  inputValue: string;
  onInputChange: (value: string) => void;
};

const Navigation = memo(function Navigation(props: NavigationProps) {
  const isStickerFairyTab = (item: any) =>
    String(item?.emoji_category_name || "")
      .toLowerCase()
      .includes("sticker fairy");

  return (
    <div className="sticker-tabs-navigation">
      <div className="sticker-tabs-toolbar">
        <div className="sticker-tabs-search">
          <div className="input-group bg-white border rounded-pill p-1">
            <input
              placeholder="Search ..."
              aria-label="Search stickers"
              aria-describedby="button-addon4"
              className="form-control border-0"
              value={props.inputValue}
              onChange={(event) => props.onInputChange(event.target.value)}
            />
          </div>
        </div>
        <div className="sticker-tabs-sort">
          <h6 className="sticker-tabs-sort-label">Sorted By</h6>
          <div>
            <select
              className="form-select"
              aria-label="Sticker sort order"
              value={props.sortedBy}
              onChange={(event) => {
                const nextSort = normalizeStickerSortBy(event.target.value);
                localStorage.setItem("sortedBy", nextSort);
                props.setSortedBy(nextSort);
              }}
            >
              <option value={"name"}>Name</option>
              <option value={"date"}>Date</option>
            </select>
          </div>
        </div>
      </div>
      <div className="sticker-tabs-categories">
        <ul className="nav nav-pills sticker-tabs-nav">
          {props.tabs.map((item: any) => (
            <li key={item?.id} className={`nav-item`}>
              <span
                className={`nav-link sticker-tabs-nav-link ${
                  props.activeTabId === item?.id ? "active" : ""
                }`}
                onClick={() => props.onNavClick(item?.id)}
              >
                <span>{item.emoji_category_name}</span>
                {isStickerFairyTab(item) && (
                  <button
                    type="button"
                    className="sticker-fairy-quick-toggle"
                    aria-label="Create Sticker Fairy sticker"
                    aria-expanded={props.showStickerFairyQuickCreate}
                    onClick={(event) => {
                      event.stopPropagation();
                      props.setShowStickerFairyQuickCreate((open: boolean) => !open);
                      props.onNavClick(item?.id);
                    }}
                  >
                    +
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
});

export const StickerTabs: React.FC<StickerTabPropsType> = ({
  tabData,
  onClickSticker,
  onStickerInserted,
}) => {
  const orderedTabs = useMemo(() => {
    const normalizedTabs = Array.isArray(tabData) ? tabData : [];
    const stickerFairyTabs = normalizedTabs.filter((tab: any) =>
      String(tab?.emoji_category_name || "")
        .toLowerCase()
        .includes("sticker fairy")
    );
    const otherTabs = normalizedTabs.filter(
      (tab: any) => !stickerFairyTabs.includes(tab)
    );
    return [...stickerFairyTabs, ...otherTabs];
  }, [tabData]);
  const [activeTabId, setActiveTab] = useState(orderedTabs[0]?.id);
  const [inputValue, setInputValue] = useState("");
  const [gridFilter, setGridFilter] = useState("");
  const [showStickerFairyQuickCreate, setShowStickerFairyQuickCreate] = useState(false);
  const [insertedCount, setInsertedCount] = useState(0);
  const [recentStickerKey, setRecentStickerKey] = useState("");
  const feedbackTimerRef = useRef<number | null>(null);
  const [sortedBy, setSortedBy] = useState<StickerSortBy>(() =>
    normalizeStickerSortBy(localStorage.getItem("sortedBy"))
  );
  const showInsertFeedback = Boolean(onStickerInserted);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    startTransition(() => {
      setGridFilter(value);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current !== null) {
        window.clearTimeout(feedbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      orderedTabs.length > 0 &&
      !orderedTabs.some((tab: any) => tab?.id === activeTabId)
    ) {
      setActiveTab(orderedTabs[0]?.id);
    }
  }, [activeTabId, orderedTabs]);
  const activeTab = useMemo(
    () => orderedTabs.find((tab: any) => tab?.id === activeTabId),
    [activeTabId, orderedTabs]
  );
  const tabItems = useMemo(
    () => (Array.isArray(activeTab?.getemojidata) ? activeTab.getemojidata : []),
    [activeTab]
  );
  const allItems = useMemo(() => {
    return orderedTabs.flatMap((item: any) => item?.getemojidata || []);
  }, [orderedTabs]);
  const sortedTabItems = useMemo(
    () => sortStickerCollectionOnce(tabItems, sortedBy),
    [sortedBy, tabItems]
  );
  const searchAllTabs = gridFilter.trim().length >= SEARCH_ALL_TABS_MIN_CHARS;
  const sortedAllItems = useMemo(() => {
    if (!searchAllTabs) {
      return sortedTabItems;
    }
    return sortStickerCollectionOnce(allItems, sortedBy);
  }, [allItems, searchAllTabs, sortedBy, sortedTabItems]);
  const searchSource = getStickerSearchSource(
    sortedTabItems,
    sortedAllItems,
    gridFilter
  );
  const visibleStickers = useMemo(
    () => filterSortedStickers(searchSource, gridFilter),
    [gridFilter, searchSource]
  );
  const getStickerKey = (productImage: string, item?: StickerSearchItem) =>
    getStickerItemKey(
      {
        product_id: item?.product_id,
        product_image: productImage || item?.product_image,
      },
      0
    );
  const notifyStickerInserted = () => {
    if (!onStickerInserted) {
      return;
    }

    requestAnimationFrame(() => onStickerInserted());
  };
  const showStickerInsertedFeedback = (productImage: string, item?: any) => {
    if (!showInsertFeedback) {
      return;
    }

    const stickerKey = getStickerKey(productImage, item);
    setInsertedCount((count) => count + 1);
    setRecentStickerKey(stickerKey);

    if (feedbackTimerRef.current !== null) {
      window.clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = window.setTimeout(() => {
      setRecentStickerKey((currentKey) =>
        currentKey === stickerKey ? "" : currentKey
      );
      feedbackTimerRef.current = null;
    }, 1200);
  };

  const insertStickerProductImage = (
    productImage: string,
    item?: any,
    closeAfterInsert = false
  ) => {
    if (!productImage) {
      return;
    }

    const stickerImageUrl = getStickerProductImageUrl(productImage);
    if (closeAfterInsert) {
      flushSync(() => onClickSticker(stickerImageUrl, item));
    } else {
      onClickSticker(stickerImageUrl, item);
    }
    showStickerInsertedFeedback(productImage, item);

    if (closeAfterInsert) {
      notifyStickerInserted();
    }
  };

  const onQuickCreateStickerReady = (productImage: string, item?: any) => {
    insertStickerProductImage(productImage, item, true);
    setShowStickerFairyQuickCreate(false);
  };

  return (
    <div className="com-box-main sticker-tabs-panel">
      <div className="post-sec">
        <div className="tabs">
          <Navigation
            tabs={orderedTabs}
            onNavClick={setActiveTab}
            activeTabId={activeTabId}
            showStickerFairyQuickCreate={showStickerFairyQuickCreate}
            setShowStickerFairyQuickCreate={setShowStickerFairyQuickCreate}
            setSortedBy={setSortedBy}
            sortedBy={sortedBy}
            inputValue={inputValue}
            onInputChange={handleInputChange}
          />
          {showStickerFairyQuickCreate && (
            <StickerFairyQuickCreate
              stickerData={orderedTabs}
              onStickerReady={onQuickCreateStickerReady}
            />
          )}
          <StickerVirtualGrid
            items={visibleStickers}
            onAddSticker={insertStickerProductImage}
            getStickerKey={getStickerKey}
            recentStickerKey={recentStickerKey}
          />
          {showInsertFeedback && (
            <div className="sticker-tabs-feedback" aria-live="polite">
              <span className="sticker-tabs-feedback-status">
                {insertedCount === 0
                  ? "No stickers inserted yet"
                  : `${insertedCount} sticker${insertedCount === 1 ? "" : "s"} inserted`}
              </span>
              <button
                type="button"
                className="sticker-tabs-done"
                onClick={notifyStickerInserted}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(StickerTabs);
